import { SEO } from "@/components/SEO";
import { JsonLd, ORGANIZATION } from "@/components/JsonLd";
import { RenewalTerms } from "@/components/subscription/RenewalTerms";
import {
  createCheckoutSession,
  detectCurrency,
  getPricingTiers,
  type Currency,
  type PricingTier,
  type StripeTierName,
} from "@/services/stripe";
import { useAuthStore } from "@/stores/authStore";
import { ArrowRight, Check, Plane, Shield, Users, Zap } from "lucide-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Link, useSearchParams } from "react-router-dom";

export function PricingPage() {
  const { user, token } = useAuthStore();
  const [searchParams] = useSearchParams();
  const [tiers, setTiers] = useState<PricingTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [currency, setCurrency] = useState<Currency>(detectCurrency);

  const canceled = searchParams.get("canceled") === "true";
  const currencySymbol = currency === "eur" ? "€" : "$";

  useEffect(() => {
    async function fetchPricing() {
      try {
        const data = await getPricingTiers(currency);
        setTiers(data);
      } catch {
        toast.error("Failed to load pricing information");
      } finally {
        setLoading(false);
      }
    }
    fetchPricing();
  }, [currency]);

  useEffect(() => {
    if (canceled) {
      toast.error("Payment canceled. You can try again anytime!");
    }
  }, [canceled]);

  const handleSubscribe = async (tierName: StripeTierName) => {
    if (!user) {
      // The choice travels with the visitor (utils/planIntent.ts): checkout
      // opens for it as soon as they have an account.
      window.location.href = `/traveler/register?plan=${tierName.toLowerCase()}`;
      return;
    }
    if (!token) {
      toast.error("Authentication required");
      return;
    }
    setCheckoutLoading(tierName);
    try {
      if (!(await createCheckoutSession(tierName, token, currency)))
        setCheckoutLoading(null);
    } catch (error) {
      // Same reasoning as the in-app plans page: say what went wrong when the
      // server has told us.
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : "Failed to start checkout. Please try again.",
      );
      setCheckoutLoading(null);
    }
  };

  const getTier = (name: string) => tiers.find((t) => t.name === name);

  const singleTripTiers = tiers.filter((t) => t.billingType === "one_time");

  // Rendered while the tiers load as well: the page names itself and its
  // canonical before the API answers, not after.
  const seo = (
    <SEO
      title="Pricing - Choose Your Plan"
      description="Choose the perfect HealthPass plan for your travel health needs."
      keywords="healthpass pricing, travel health subscription"
      url="https://travelhealthpass.app/pricing"
    />
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        {seo}
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {seo}
      {/* Offers from the tiers this page displays, in the shown currency. */}
      <JsonLd
        data={{
          "@type": "SoftwareApplication",
          name: "HealthPass",
          applicationCategory: "HealthApplication",
          operatingSystem: "Web, Android, iOS (PWA)",
          publisher: { "@id": ORGANIZATION["@id"] },
          offers: tiers.map((tier) => ({
            "@type": "Offer",
            name: tier.displayName,
            price: tier.price,
            priceCurrency: tier.currency.toUpperCase(),
          })),
        }}
      />

      {/* Header */}
      {/* pt-28, not py-20: the global OfflineBanner is fixed at top-16 and
          covered this heading whenever it was showing. */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white pb-20 pt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-blue-100">
            Start free. Upgrade anytime. Cancel anytime.
          </p>
          {/* Currency Toggle */}
          <div className="mt-8 inline-flex items-center gap-1 bg-white/20 rounded-full p-1">
            <button
              onClick={() => setCurrency("usd")}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition ${
                currency === "usd"
                  ? "bg-white text-blue-700 shadow"
                  : "text-white hover:bg-white/10"
              }`}
            >
              $ USD
            </button>
            <button
              onClick={() => setCurrency("eur")}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition ${
                currency === "eur"
                  ? "bg-white text-blue-700 shadow"
                  : "text-white hover:bg-white/10"
              }`}
            >
              € EUR
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Monthly Subscriptions */}
        <div className="text-center mb-10">
          <h2 className="text-2xl font-bold text-gray-900">
            Monthly Subscriptions
          </h2>
          <p className="text-gray-500 mt-1">
            Cancel anytime. 14-day free trial on paid plans.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-20">
          {/* FREE */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="h-6 w-6 text-gray-600" />
              <h3 className="text-2xl font-bold text-gray-900">FREE</h3>
            </div>
            <div className="mb-6">
              <span className="text-4xl font-bold text-gray-900">
                {currencySymbol}0
              </span>
              <span className="text-gray-500 ml-1">/forever</span>
            </div>
            <ul className="space-y-3 mb-8">
              {(getTier("FREE")?.features || []).map((f, i) => (
                <li key={i} className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{f}</span>
                </li>
              ))}
            </ul>
            <Link
              to={user ? "/dashboard" : "/traveler/register"}
              className="block w-full bg-gray-100 text-gray-900 px-6 py-3 rounded-lg hover:bg-gray-200 transition font-semibold text-center"
            >
              {user ? "Current Plan" : "Start Free"}
            </Link>
          </div>

          {/* PLUS */}
          <div className="bg-white rounded-2xl shadow-2xl p-8 border-4 border-blue-600 relative transform md:scale-105">
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <span className="bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                Most Popular
              </span>
            </div>
            <div className="flex items-center gap-2 mb-4">
              <Zap className="h-6 w-6 text-blue-600" />
              <h3 className="text-2xl font-bold text-gray-900">PLUS</h3>
            </div>
            <div className="mb-6">
              <span className="text-4xl font-bold text-gray-900">
                {currencySymbol}
                {getTier("PLUS")?.price || 9}
              </span>
              <span className="text-gray-500 ml-1">/month</span>
            </div>
            <ul className="space-y-3 mb-8">
              {(getTier("PLUS")?.features || []).map((f, i) => (
                <li key={i} className="flex items-start">
                  <Check className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{f}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => handleSubscribe("PLUS")}
              disabled={checkoutLoading === "PLUS"}
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition font-semibold flex items-center justify-center group disabled:opacity-50"
            >
              {checkoutLoading === "PLUS"
                ? "Redirecting..."
                : "Try 14 Days Free"}
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition" />
            </button>
            <RenewalTerms
              kind="recurring"
              price={`${currencySymbol}${getTier("PLUS")?.price ?? ""}`}
              className="mt-3 text-center"
            />
          </div>

          {/* PREMIUM */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <Users className="h-6 w-6 text-purple-600" />
              <h3 className="text-2xl font-bold text-gray-900">PREMIUM</h3>
            </div>
            <div className="mb-6">
              <span className="text-4xl font-bold text-gray-900">
                {currencySymbol}
                {getTier("PREMIUM")?.price || 15}
              </span>
              <span className="text-gray-500 ml-1">/month</span>
            </div>
            <ul className="space-y-3 mb-8">
              {(getTier("PREMIUM")?.features || []).map((f, i) => (
                <li key={i} className="flex items-start">
                  <Check className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{f}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => handleSubscribe("PREMIUM")}
              disabled={checkoutLoading === "PREMIUM"}
              className="w-full bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition font-semibold flex items-center justify-center group disabled:opacity-50"
            >
              {checkoutLoading === "PREMIUM"
                ? "Redirecting..."
                : "Try 14 Days Free"}
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition" />
            </button>
            <RenewalTerms
              kind="recurring"
              price={`${currencySymbol}${getTier("PREMIUM")?.price ?? ""}`}
              className="mt-3 text-center"
            />
          </div>
        </div>

        {/* Single Trip Plans */}
        {singleTripTiers.length > 0 && (
          <>
            <div className="text-center mb-10">
              <h2 className="text-2xl font-bold text-gray-900">
                Single Trip Plans
              </h2>
              <p className="text-gray-500 mt-1">
                One-time payment. Full access for 90 days — no subscription
                needed.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-20">
              {/* PLUS SINGLE */}
              {getTier("PLUS_SINGLE") && (
                <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-emerald-600 relative">
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-emerald-700 text-white px-4 py-1 rounded-full text-sm font-medium">
                      Best for one trip
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mb-4">
                    <Plane className="h-6 w-6 text-emerald-600" />
                    <h3 className="text-2xl font-bold text-gray-900">
                      PLUS – SINGLE TRIP
                    </h3>
                  </div>
                  <div className="mb-2">
                    <span className="text-4xl font-bold text-gray-900">
                      {currencySymbol}
                      {getTier("PLUS_SINGLE")?.price || 25}
                    </span>
                    <span className="text-gray-500 ml-1">/one trip</span>
                  </div>
                  <span className="inline-block mb-6 bg-emerald-50 text-emerald-700 text-sm font-medium px-3 py-1 rounded-full">
                    90 days access
                  </span>
                  <ul className="space-y-3 mb-8">
                    {(getTier("PLUS_SINGLE")?.features || []).map((f, i) => (
                      <li key={i} className="flex items-start">
                        <Check className="h-5 w-5 text-emerald-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => handleSubscribe("PLUS_SINGLE")}
                    disabled={checkoutLoading === "PLUS_SINGLE"}
                    className="w-full bg-emerald-700 text-white px-6 py-3 rounded-lg hover:bg-emerald-800 transition font-semibold disabled:opacity-50"
                  >
                    {checkoutLoading === "PLUS_SINGLE"
                      ? "Redirecting..."
                      : "Buy Single Trip"}
                  </button>
                  <RenewalTerms
                    kind="one_time"
                    price={`${currencySymbol}${getTier("PLUS_SINGLE")?.price ?? ""}`}
                    className="mt-3 text-center"
                  />
                </div>
              )}

              {/* PREMIUM SINGLE */}
              {getTier("PREMIUM_SINGLE") && (
                <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-gray-200">
                  <div className="flex items-center gap-2 mb-4">
                    <Plane className="h-6 w-6 text-purple-600" />
                    <h3 className="text-2xl font-bold text-gray-900">
                      PREMIUM – SINGLE TRIP
                    </h3>
                  </div>
                  <div className="mb-2">
                    <span className="text-4xl font-bold text-gray-900">
                      {currencySymbol}
                      {getTier("PREMIUM_SINGLE")?.price || 39}
                    </span>
                    <span className="text-gray-500 ml-1">/one trip</span>
                  </div>
                  <span className="inline-block mb-6 bg-emerald-50 text-emerald-700 text-sm font-medium px-3 py-1 rounded-full">
                    90 days access
                  </span>
                  <ul className="space-y-3 mb-8">
                    {(getTier("PREMIUM_SINGLE")?.features || []).map((f, i) => (
                      <li key={i} className="flex items-start">
                        <Check className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => handleSubscribe("PREMIUM_SINGLE")}
                    disabled={checkoutLoading === "PREMIUM_SINGLE"}
                    className="w-full bg-gray-100 text-gray-900 px-6 py-3 rounded-lg hover:bg-gray-200 transition font-semibold disabled:opacity-50"
                  >
                    {checkoutLoading === "PREMIUM_SINGLE"
                      ? "Redirecting..."
                      : "Buy Single Trip"}
                  </button>
                  <RenewalTerms
                    kind="one_time"
                    price={`${currencySymbol}${getTier("PREMIUM_SINGLE")?.price ?? ""}`}
                    className="mt-3 text-center"
                  />
                </div>
              )}
            </div>
          </>
        )}

        {/* Trust Signals */}
        <div className="text-center">
          <p className="text-gray-600 mb-4">Trusted by travelers worldwide</p>
          <div className="flex items-center justify-center space-x-8">
            <div className="flex items-center space-x-2">
              <Check className="h-5 w-5 text-green-600" />
              <span className="text-sm text-gray-700">GDPR Compliant</span>
            </div>
            <div className="flex items-center space-x-2">
              <Check className="h-5 w-5 text-green-600" />
              <span className="text-sm text-gray-700">SSL Encrypted</span>
            </div>
            <div className="flex items-center space-x-2">
              <Check className="h-5 w-5 text-green-600" />
              <span className="text-sm text-gray-700">Stripe Secure</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
